import React, { useEffect, useState, useMemo } from 'react'
import { Chip, FormControl, FormLabel, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { useStyles } from './style'
import { ChevronDown, CircleX } from 'lucide-react'

// Definição da opção "Todos" com um ID especial que não deve conflitar com IDs reais.
const ALL_QUEUES_OPTION = {
  id: 'all',
  name: 'TODOS',
  // color: '#9E9E9E', // Cor opcional para identificação
  isBot: false // Pode ser falso, mas seu ID é o que importa
}

// Adicionadas as props filterByIsBot e includeAllOption
const QueueSelect = ({
  selectedQueueIds = [],
  onChange,
  filterByIsBot, // Receber a prop de filtro
  includeAllOption, // Receber a prop da opção 'Todos'
  label = 'Departamentos', // Adiciona um label default
  isMulti = true,
  disabled = false
}) => {
  const classes = useStyles()
  const [queues, setQueues] = useState([])
  const [selected, setSelected] = useState([])
  const [autocompleteOptions, setAutocompleteOptions] = useState([])
  const componentId = useMemo(
    () => `queue-select-${Math.random().toString(36).substring(2, 9)}`,
    []
  )

  // 1. Efeito para buscar as filas e aplicar o filtro inicial
  useEffect(() => {
    const fetchQueues = async () => {
      try {
        const { data } = await api.get('/queue')
        setQueues(data)
      } catch (err) {
        toastError(err)
      }
    }

    fetchQueues()
  }, [])

  // 2. Efeito para filtrar e montar as opções do Autocomplete (incluindo "Todos")
  useEffect(() => {
    let filteredQueues = queues

    // Aplica o filtro isBot, se a prop for definida
    if (filterByIsBot === true) {
      filteredQueues = queues.filter(q => q.isBot === false)
    } else if (filterByIsBot === false) {
      filteredQueues = queues.filter(q => q.isBot === true)
    }

    // Inicializa as opções com as filas filtradas
    let options = [...filteredQueues]

    // Adiciona a opção "Todos" se a prop for verdadeira
    if (includeAllOption) {
      // Garantir que "Todos" seja a primeira opção
      options.unshift(ALL_QUEUES_OPTION)
    }

    setAutocompleteOptions(options)
  }, [queues, filterByIsBot, includeAllOption])

  // 3. Efeito para sincronizar as filas selecionadas (objetos) com os IDs (do Formik)
  useEffect(() => {
    if (queues.length > 0) {
      const normalizedIds = selectedQueueIds.map(id => Number(id))
      let selectableQueues = queues
      if (filterByIsBot === true) {
        selectableQueues = queues.filter(q => q.isBot === false)
      } else if (filterByIsBot === false) {
        selectableQueues = queues.filter(q => q.isBot === true)
      }
      // Filtra as filas existentes no estado 'queues' que estão nos IDs selecionados.
      let newSelected = selectableQueues.filter(queue =>
        normalizedIds.includes(Number(queue.id))
      )

      // Adiciona a opção "TODOS" se todos os IDs filtrados estiverem selecionados.
      const filteredQueueIds = selectableQueues.map(q => q.id)

      const allFilteredSelected =
        filteredQueueIds.every(id => selectedQueueIds.includes(id)) &&
        filteredQueueIds.length > 0

      if (includeAllOption && allFilteredSelected) {
        // Garantir que 'TODOS' esteja na lista de selecionados se todos os outros estão.
        if (!newSelected.some(q => q.id === ALL_QUEUES_OPTION.id)) {
          newSelected = [ALL_QUEUES_OPTION, ...newSelected]
        }
      } else {
        // Remover 'TODOS' se não estiverem todos selecionados ou se o ALL_QUEUES_OPTION ainda estiver no selected por algum motivo.
        newSelected = newSelected.filter(q => q.id !== ALL_QUEUES_OPTION.id)
      }

      setSelected(newSelected)
    } else if (selectedQueueIds.length === 0) {
      setSelected([])
    }
  }, [selectedQueueIds, queues, filterByIsBot, includeAllOption]) // Dependências para reexecução

  // 4. Manipulador de Mudança para gerenciar a seleção de "Todos"
  const handleChange = (event, value) => {
    if (!isMulti) {
      setSelected(value ? [value] : [])
      if (onChange) {
        onChange(value ? [value.id] : [])
      }
      return
    }

    const values = Array.isArray(value) ? value : value ? [value] : []
    let newSelectedQueues = [...values]
    let newSelectedIds = []

    let relevantQueues = queues
    if (filterByIsBot === true) {
      relevantQueues = queues.filter(q => q.isBot === false)
    } else if (filterByIsBot === false) {
      relevantQueues = queues.filter(q => q.isBot === true)
    }
    const relevantQueueIds = relevantQueues.map(q => q.id)

    // Lógica para a opção "Todos"
    if (includeAllOption) {
      const isAllSelected = values.some(v => v.id === ALL_QUEUES_OPTION.id)
      const wasAllPreviouslySelected = selected.some(
        v => v.id === ALL_QUEUES_OPTION.id
      )

      if (isAllSelected && !wasAllPreviouslySelected) {
        // Se "Todos" foi selecionado (e não estava), seleciona todas as filas relevantes
        newSelectedQueues = [ALL_QUEUES_OPTION, ...relevantQueues]
        newSelectedIds = relevantQueueIds
      } else if (
        isAllSelected &&
        values.length === 1 &&
        values[0].id === ALL_QUEUES_OPTION.id
      ) {
        // Se "Todos" foi selecionado e o usuário desmarcou as outras (deixando apenas "Todos"), desmarca tudo.
        newSelectedQueues = []
        newSelectedIds = []
      } else if (wasAllPreviouslySelected && !isAllSelected) {
        // Se "Todos" foi desmarcado, remove a opção 'Todos' da lista
        newSelectedQueues = values.filter(v => v.id !== ALL_QUEUES_OPTION.id)
        newSelectedIds = newSelectedQueues.map(q => q.id)
      } else if (isAllSelected && values.length < relevantQueues.length + 1) {
        // Se "Todos" estava selecionado, mas uma fila individual foi desmarcada, remove a opção "Todos".
        newSelectedQueues = values.filter(v => v.id !== ALL_QUEUES_OPTION.id)
        newSelectedIds = newSelectedQueues.map(q => q.id)
      } else if (
        values.length === relevantQueues.length &&
        relevantQueues.length > 0 &&
        values.every(q => relevantQueueIds.includes(q.id))
      ) {
        // Se o número de seleções for igual ao número de filas relevantes, adicione a opção "Todos"
        newSelectedQueues = [ALL_QUEUES_OPTION, ...values]
        newSelectedIds = relevantQueueIds // Seleciona todos os IDs relevantes
      } else {
        // Seleção normal de itens individuais
        newSelectedIds = values
          .filter(v => v.id !== ALL_QUEUES_OPTION.id)
          .map(q => q.id)
      }
    } else {
      // Se a opção "Todos" não está incluída, apenas mapeia os IDs
      newSelectedIds = values.map(queue => queue.id)
    }

    // Atualiza o estado local e chama a função de alteração do pai (Formik)
    setSelected(newSelectedQueues)
    if (onChange) {
      // Garante que o array de IDs enviado ao Formik só contenha IDs numéricos de filas reais.
      onChange(
        newSelectedIds.filter(
          id => id !== ALL_QUEUES_OPTION.id && typeof id === 'number'
        )
      )
    }
  }

  // 5. Filtrar as opções disponíveis no menu suspenso para não incluir as já selecionadas,
  //    e garantir que a opção "Todos" esteja sempre disponível se não estiver selecionada.
  const optionsToDisplay = autocompleteOptions
    .filter(
      option =>
        !selected.some(sel => sel.id === option.id) ||
        option.id === ALL_QUEUES_OPTION.id
    )
    .filter(option => {
      // Para Autocomplete, se o item "Todos" já foi selecionado, ele não deve aparecer nas opções, a menos que ele seja o único selecionado.
      if (
        option.id === ALL_QUEUES_OPTION.id &&
        selected.some(sel => sel.id === ALL_QUEUES_OPTION.id)
      ) {
        return false
      }
      return true
    })

  return (
    <FormControl className={classes.formControl}>
      <FormLabel htmlFor={componentId}>{label}</FormLabel>
      <Autocomplete
        multiple={isMulti}
        size="small"
        disabled={disabled}
        // Usa optionsToDisplay para a lista de opções
        options={optionsToDisplay}
        noOptionsText="Não há mais departamentos disponíveis"
        value={isMulti ? selected : selected[0] || null}
        onChange={handleChange}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) => option.id === value.id}
        popupIcon={<ChevronDown />}
        renderTags={(value, getTagProps) =>
          value
            .filter(option => option.id !== ALL_QUEUES_OPTION.id) // Não renderiza o chip "TODOS" aqui para evitar redundância, mas mantém a cor/estilo de uma das filas selecionadas
            .map((option, index) => (
              <Chip
                key={option.id}
                variant="outlined"
                className={classes.chip}
                style={{
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  height: '20px',
                  fontSize: '11px'
                  // backgroundColor: option.id === ALL_QUEUES_OPTION.id ? ALL_QUEUES_OPTION.color : option.color,
                }}
                label={option.name}
                {...getTagProps({ index })}
                size="small"
                deleteIcon={<CircleX />}
              />
            ))
        }
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selected.length === 0 ? 'Selecione' : ''}
            fullWidth
            disabled={disabled}
            inputProps={{ ...params.inputProps, id: componentId }}
          />
        )}
      />
    </FormControl>
  )
}

export default QueueSelect
